import React from 'react';
// material-ui
import {
    Alert,
    Button,
    FormHelperText,
    List,
    ListItemAvatar,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Stack,
    Typography,
    Dialog,
    Input,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TextField,
    Box,
    DialogTitle,
    Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import MainCard from 'components/MainCard';
import { deleteRequest, getRequest, postRequest } from 'utils/apiRequest';

import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import { Skeleton } from '../../../node_modules/@mui/lab/index';
import { UserAuthContext } from 'context/UserAuthContext';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { useNavigate } from 'react-router';
import { Settings, SettingsTwoTone } from '@mui/icons-material';
import HelpTip from 'components/HelpTip';



const AddKeySchema = Yup.object().shape({
    domain: Yup.string().max(255).matches(/^(?!:\/\/)(?=.{1,255}$)((.{1,63}\.){1,127}(?![0-9]*$)[a-z0-9-]+\.?)$/i, { message: 'Invalid domain format' }).required('A domain name is required and must be 255 characters or less'),
})


const ClientSecretDisplay = ({ secretKey }) => {
    const [secret, setSecret] = React.useState(Array(secretKey.length).fill('*').join(''));

    const showSecret = () => {
        setSecret(secretKey);
    }
    return (
        <button onClick={() => showSecret()} style={{ cursor: 'pointer', padding: 0, border: 0, background: 'transparent' }}>
            {"API Secret: " + secret}
        </button>
    )
}

const CredentialsLoading = () => {
    return (
        <MainCard title={"Credentials"}>
            <List
                component="nav"
                sx={{
                    px: 0,
                    py: 0,
                }}
            >
                {Array(3).fill(0).map((n, i) => {
                    return (
                        <ListItem divider key={i}>
                            <ListItemText
                                primary={
                                    <Skeleton variant="text" width="40%" />
                                }
                                secondary={
                                    <>
                                        <Skeleton variant="text" width="40%" />
                                        <Skeleton variant="text" width="35%" />
                                        <Skeleton variant="text" width="45%" />
                                    </>
                                }
                            />
                            <Box align="right">
                                <Box display="grid" gap={2} gridTemplateColumns="1fr 1fr">
                                    <Skeleton variant="rectangle" width="50px" />
                                    <Skeleton variant="rectangle" width="50px" />
                                </Box>
                            </Box>
                        </ListItem>
                    )
                })}
            </List>
        </MainCard>)
}

const Credentials = () => {
    const { getSubscriptionName } = React.useContext(UserAuthContext);
    const [clients, setClients] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [successMessage, setSuccessMessage] = React.useState(null);
    const [showAddKeyModal, setShowAddKeyModal] = React.useState(false);

    const getClientKeys = () => {
        setLoading(true)
        getRequest('/clients').then(data => {
            setClients(data);
        }).finally(() => {
            setLoading(false);
        })
    }
    React.useEffect(() => {
        getClientKeys();
    }, [])

    const handleDeleteCredentials = (clientId) => {
        var result = window.confirm("Are you sure you want to delete this?");
        if (result === true) {
            deleteRequest('/client/delete/' + clientId).then(data => {
                getClientKeys();
            })
        }
    }

    const handleAddKeyModal = (wh) => {
        setSuccessMessage(null);
        setShowAddKeyModal(true);
    }
    const handleAddKeyModalClose = () => {
        setShowAddKeyModal(false);
    }
    const showAddButton = () => {
        let maxKeys = 1;
        switch (getSubscriptionName().toLowerCase()) {
            case "standard": maxKeys = 3; break;
            case "premium": maxKeys = 10; break;
            case "enterprise": maxKeys = 1000; break;
        }
        if (clients.length < maxKeys) {
            return <Button onClick={() => handleAddKeyModal()} variant="outlined">Add Key</Button>
        }
        return <></>
    }
    if (loading) {
        return <CredentialsLoading />
    }

    return (<>
            {successMessage && (
                <Alert severity="success" sx={{mb:3}}>{successMessage}</Alert>
            )}
        <MainCard title={"Credentials"} secondary={showAddButton()}>
            <List
                component="nav"
                sx={{
                    px: 0,
                    py: 0,
                }}
            >
                {clients.map((client, i) => {
                    return (<>
                        <Box key={'client-'+i} display="grid" gap={1} gridTemplateColumns="4fr 1fr" p={2} alignItems="center">
                            <ListItemText
                                primary={
                                    <Typography variant="subtitle1">API Key: {client.client_key}</Typography>
                                }
                                secondary={
                                    <>
                                        <ClientSecretDisplay secretKey={client.client_secret} />
                                        <span style={{ display: 'block' }}>Allowed Domain: {client.domain}</span>
                                        <span style={{ display: 'block' }}>Created At: {client.created_at.substring(0, client.created_at.indexOf('T'))}</span>
                                    </>
                                }
                            />
                            <Box align="right">
                                <Box display="grid" gap={2} gridTemplateColumns="1fr 1fr">
                                        <CredentialSettingsDialog client={client} onSuccess={()=>{
                                            getClientKeys()
                                            setSuccessMessage("Settings saved successfully");
                                        }} />
                                        <Button variant="outlined" color="error" onClick={() => handleDeleteCredentials(client.id)}>
                                            <DeleteTwoToneIcon />
                                        </Button>
                                </Box>
                            </Box>
                        </Box>
                            <Divider />
                            </>
                    )
                })}
            </List>
        </MainCard>

        <Dialog
            open={showAddKeyModal}
            onClose={handleAddKeyModalClose}
            size="md"
        >
            <DialogContent>
                <Formik
                    initialValues={{
                        domain: '',
                        submit: null
                    }}
                    validationSchema={AddKeySchema}
                    onSubmit={(values, { setErrors, setStatus, setSubmitting }) => {
                        setSuccessMessage(null);
                        setSubmitting(true);
                        let formData = new FormData();
                        Object.keys(values).forEach((k) => {
                            formData.append(k, values[k]);
                        })
                        postRequest('/client/create', formData).then(d => {
                            if (d.errors) {
                                const err = {}
                                Object.keys(d.errors).forEach((k) => {
                                    err[k] = d.errors[k][0];
                                })
                                setErrors(err);
                            } else {
                                setSuccessMessage("Key created successfully");
                                setShowAddKeyModal(false);
                                getClientKeys();
                            }
                        }).catch(err => {
                            console.log(err)
                        }).finally(() => {
                            setSubmitting(false);
                        })
                    }}>
                    {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                        <form noValidate onSubmit={handleSubmit}>
                            <p>What is the domain name this key will be accessed from?</p>
                            <Table>
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Domain</TableCell>
                                        <TableCell>
                                            <TextField
                                                name="domain"
                                                type="text"
                                                value={values.domain}
                                                onChange={handleChange}
                                                autoComplete="domain-name"
                                                placeholder="https://www.example.com"
                                                sx={{ width: '100%' }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <LoadingButton
                                                disableElevation
                                                disabled={isSubmitting}
                                                type="submit"
                                                size="small"
                                                variant="contained"
                                                color="primary"
                                                loading={isSubmitting}
                                            >
                                                Submit
                                            </LoadingButton>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                            {touched.domain && errors.domain && (
                                <FormHelperText error>
                                    {errors.domain}
                                </FormHelperText>
                            )}
                        </form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    </>)
}

const SettingsSchema = Yup.object().shape({
    minFulfillmentDays: Yup.number().max(1000),
    maxFulfillmentDays: Yup.number().max(1000),
})
const CredentialSettingsDialog = ({ client, onSuccess = () => {} }) => {
    const [settingsDialogOpen, setSettingsDialogOpen] = React.useState(false);

    const handleCredentialSettings = () => {
        setSettingsDialogOpen(true);
    }
    const handleCredentialSettingsClose = () => {
        setSettingsDialogOpen(false);
    }

    return (
        <>
            <Button variant="outlined" color="primary" onClick={() => handleCredentialSettings(client.id)}>
                <SettingsTwoTone />
            </Button>

            <Dialog
                open={settingsDialogOpen}
                onClose={handleCredentialSettingsClose}
                fullWidth={false}
                size="sm"
            >
                <DialogTitle>Client Key Settings</DialogTitle>
                <DialogContent sx={{maxWidth: 500}}>
                    <Formik
                        initialValues={{
                            minFulfillmentDays: (client.settings && Math.floor(client.settings.min_fulfillment_hours / 24)) ?? 0,
                            maxFulfillmentDays: (client.settings && Math.floor(client.settings.max_fulfillment_hours / 24)) ??0,
                            clientKey: client.client_key
                        }}
                        validationSchema={SettingsSchema}
                        onSubmit={(values, { setErrors, setStatus, setSubmitting }) => {
                            setSubmitting(true);
                            let formData = new FormData();
                            Object.keys(values).forEach((k) => {
                                formData.append(k, values[k]);
                            })
                            postRequest('/client/settings/update', formData).then(d => {
                                if (d.errors) {
                                    const err = {}
                                    Object.keys(d.errors).forEach((k) => {
                                        err[k] = d.errors[k][0];
                                    })
                                    setErrors(err);
                                } else {
                                    onSuccess();
                                    // setSettingsDialogOpen(false);
                                }
                            }).catch(err => {
                                console.log(err)
                            }).finally(() => {
                                setSubmitting(false);
                            })
                        }}>
                        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                            <form noValidate onSubmit={handleSubmit}>
                                <Typography variant="caption" display="block" sx={{mb: 3}}>Set some "guard rails" on your delivery time messages. You may want to make sure you never display a time less than or greater than X days. <br /><br />If any warehouse, carrier, or fulfillment delays cause the estimate to be less than the minumum set below, we will display at minimum date to the customer.<br /><br />If any warehouse, carrier, or fulfillment delays cause the estimate to be greater than the maximum set below, we will cap the displayed estimate to the maximum. </Typography>
                                <Box display="grid" gap={1} gridTemplateColumns="1fr">
                                    <Box display="grid" gap={1} gridTemplateColumns="1.25fr 1fr" alignItems="center">
                                        <Box sx={{alignItems: 'center'}}>
                                            <Box component="label" sx={{ fontWeight: 'bold' }}>Minimum Lead Time (in days)</Box>
                                            <HelpTip title={"Delivery times will not be shorter than this many days"} />
                                        </Box>
                                        <Box>
                                            <TextField
                                                name="minFulfillmentDays"
                                                type="number"
                                                value={values.minFulfillmentDays}
                                                onChange={handleChange}
                                                autoComplete="min-lead-time-days"
                                            />
                                            {touched.minFulfillmentDays && errors.minFulfillmentDays && (
                                                <FormHelperText error>
                                                    {errors.minFulfillmentDays}
                                                </FormHelperText>
                                            )}
                                        </Box>
                                    </Box>
                                    <Box display="grid" gap={1} gridTemplateColumns="1.25fr 1fr" alignItems="center">
                                        <Box sx={{alignItems: 'center'}}>
                                            <Box component="label" sx={{ fontWeight: 'bold' }}>Maximum Lead Time (in days)</Box>
                                            <HelpTip title={"Delivery times will not be longer than this many days"} />
                                        </Box>
                                        <Box>
                                            <TextField
                                                name="maxFulfillmentDays"
                                                type="number"
                                                value={values.maxFulfillmentDays}
                                                onChange={handleChange}
                                                autoComplete="max-lead-time-days"
                                            />
                                            {touched.maxFulfillmentDays && errors.maxFulfillmentDays && (
                                                <FormHelperText error>
                                                    {errors.maxFulfillmentDays}
                                                </FormHelperText>
                                            )}
                                        </Box>
                                    </Box>
                                    <Box display="grid" gap={1} gridTemplateColumns="1fr">
                                        <LoadingButton
                                            disableElevation
                                            disabled={isSubmitting}
                                            type="submit"
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            loading={isSubmitting}
                                        >
                                            Submit
                                        </LoadingButton>
                                    </Box>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>
        </>
    )
}

export default Credentials;